import { useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { BREAKPOINTS_WIDTHS, useWindowSize } from '@belong/common';
import { Image, Text } from '@belong/ui';
import { sendTextMessage } from 'store/redux/user/actions';
import { selectFullUser } from 'store/redux/user/selectors';

type MobileBannerProps = {
  sendTextMessageAction: (message: { eventType: string; cta: string }) => void;
  title?: string;
  isInbox?: boolean;
};

const MobileBanner = ({
  sendTextMessageAction,
  title = 'Want to use a different payment method?',
  isInbox = false,
}: MobileBannerProps) => {
  const { user } = useSelector(selectFullUser);
  const [sent, setSent] = useState(false);
  const { width } = useWindowSize();
  const isMobile = width < BREAKPOINTS_WIDTHS.SM;

  const handleSendLink = async () => {
    const link = 'https://dl.bln.hm/6BG2';

    const textMessage = {
      eventType: 'AppDownloadRequest',
      cta: `Hi ${user.firstName}${user.lastName ? ' ' : ''}${
        user.lastName
      }, Team Belong here. You can use this link to download our app! ${link}`,
    };

    try {
      if (isMobile) {
        // `window.open` is typed as possibly returning `null` (a blocked
        // pop-up). Asserting keeps the existing throw, which the `catch` below
        // already handles, rather than silently skipping the focus.
        window.open(link, '_blank')!.focus();
      } else {
        await sendTextMessageAction(textMessage);
        setSent(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div
      className="mb-xl flex w-full flex-col-reverse rounded border border-solid border-green md:flex-row"
      style={{ backgroundColor: 'rgba(62, 228, 169, 0.1)' }}
    >
      <div className="flex flex-col self-center p-sm">
        {!isMobile && (
          <Text
            className="text-center text-h3-sm md:text-left"
            fontWeight="semibold"
          >
            {title}
          </Text>
        )}
        <Text className="my-sm text-center md:text-left">
          Download our app to pay early, switch payment methods, and manage your{' '}
          <strong>monthly cashflow</strong>.
        </Text>
        {!sent ? (
          <Text
            variant="body"
            fontWeight="semibold"
            className="cursor-pointer text-center md:text-left"
            onClick={() => handleSendLink()}
          >
            {isMobile ? 'DOWNLOAD NOW' : 'SEND LINK TO MY PHONE'}
          </Text>
        ) : (
          <>
            {isMobile ? (
              <Text
                variant="body"
                fontWeight="semibold"
                className="cursor-pointer text-center md:text-left"
                onClick={() => handleSendLink()}
              >
                DOWNLOAD NOW
              </Text>
            ) : (
              <Text
                variant="body"
                fontWeight="semibold"
                className="text-center md:text-left"
              >
                MESSAGE SENT!
              </Text>
            )}
          </>
        )}
      </div>
      <div className="border-0 border-b border-solid border-b-green lg:border-b-0" />
      <div className="mx-auto flex w-3/5 md:ml-auto md:self-end xl:w-2/5">
        <Image
          src={
            isInbox
              ? '/homeowner-earnings/ho_inbox_pixelated.png'
              : '/homeowner-earnings/ho_earnings.png'
          }
          alt="Homeowner Earnings Mobile"
          sizes="(min-width: 1280px) 40vw, 60vw"
        />
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  sendTextMessageAction: sendTextMessage,
};

export default connect(null, mapDispatchToProps)(MobileBanner);
